'use client';

import './economics-page.scss';

import React, { useContext, useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Rectangle,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiAlertCircle,
  FiClock,
  FiExternalLink,
  FiInfo,
} from 'react-icons/fi';
import type { Economics } from '@benzinga/calendar-economics-manager';
import type { StoryObject } from '@benzinga/advanced-news-manager';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useScrollDepthTracking } from '@benzinga/analytics';
import { Layout } from '@benzinga/core-ui';
import { CalendarNewLayoutHeader } from '../../calendars/components/CalendarNewLayoutHeader';
import type { EconomicsPageData, EconomicsRelease, SeriesPoint } from './fetchEconomicsHistory';
import { useEconomicsNews } from './useEconomicsNews';

export interface SeriesDisplayConfig {
  eventName: string;
  label: string;
  fullLabel: string;
  color?: string;
  chartGroup?: string;
  showInChart?: boolean;
}

export interface EconomicsPageConfig {
  name: string;
  shortName: string;
  subtitle: string;
  chartTitle: string;
  educationalTitle: string;
  educationalContent: React.ReactNode;
  Icon?: React.ElementType;
  source: string;
  series: SeriesDisplayConfig[];
  pageId: string;
}

const FALLBACK_COLORS = ['#2e79f6', '#10b981', '#f59e0b', '#8b5cf6'];

const getColor = (s: SeriesDisplayConfig, idx: number) => s.color ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length];

const surpriseDiff = (pt: SeriesPoint | undefined): number | null => {
  if (!pt || pt.actual == null || pt.consensus == null) return null;
  return pt.actual - pt.consensus;
};

const surpriseBadgeClass = (diff: number | null): string =>
  diff === null ? 'bg-bzgray-100 text-bzgray-500' : 'bg-bzgray-100 text-bzgray-700';

const fmt = (v: number | null | undefined, unit = '%'): string => (v == null ? 'N/A' : `${v}${unit}`);

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-bzgray-200 rounded-lg shadow-lg p-4">
      <p className="font-semibold text-bzgray-900 mb-2">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {entry.value != null ? `${entry.value}%` : 'N/A'}
        </p>
      ))}
    </div>
  );
};

const SignAwareBar = ({
  dataKeyName,
  payload,
  ...rest
}: React.ComponentProps<typeof Rectangle> & {
  dataKeyName: string;
  payload?: Record<string, unknown>;
}) => {
  const value = payload?.[dataKeyName];
  const radius: [number, number, number, number] = typeof value === 'number' && value < 0 ? [0, 0, 4, 4] : [4, 4, 0, 0];
  return <Rectangle {...rest} radius={radius} />;
};

const NewsItem = ({ article, onClick }: { article: StoryObject; onClick?: () => void }) => (
  <a
    href={article.url}
    onClick={onClick}
    target="_blank"
    rel="noopener noreferrer"
    className="group block p-4 hover:bg-bzgray-100 transition-colors"
  >
    <div className="flex items-start gap-3">
      <div className="flex-1 min-w-0">
        {article.channels?.[0]?.name && (
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-bzblue-300 text-bzblue-800 rounded mb-2">
            {article.channels[0].name}
          </span>
        )}
        <h3 className="text-sm font-semibold text-bzgray-900 line-clamp-2 mb-1 group-hover:text-bzblue-700 transition-colors">
          {article.title}
        </h3>
        {article.teaser && <p className="text-xs text-bzgray-600 line-clamp-2 mb-2">{article.teaser}</p>}
        <div className="flex items-center gap-2 text-xs text-bzgray-500">
          {article.author && <span>{article.author}</span>}
          {article.author && article.created && <span>·</span>}
          {article.created && (
            <span>
              {new Date(article.created).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
          )}
        </div>
      </div>
      <FiExternalLink className="w-4 h-4 text-bzgray-400 flex-shrink-0 mt-1 group-hover:text-bzblue-700 transition-colors" />
    </div>
  </a>
);

const SingleSeriesStatCards = ({
  latest,
  seriesCfg,
  nextRelease,
}: {
  latest: EconomicsRelease;
  seriesCfg: SeriesDisplayConfig;
  nextRelease: Economics | null;
}) => {
  const pt = latest.series[seriesCfg.eventName];
  const diff = surpriseDiff(pt);
  const trend: 'rising' | 'falling' | 'flat' | null =
    pt?.actual != null && pt?.prior != null
      ? pt.actual > pt.prior
        ? 'rising'
        : pt.actual < pt.prior
          ? 'falling'
          : 'flat'
      : null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-white border border-bzgray-200 rounded-lg p-4">
        <div className="text-xs font-semibold text-bzgray-700 uppercase tracking-wide mb-1">Actual</div>
        <div className="text-2xl font-bold text-bzgray-900">{fmt(pt?.actual)}</div>
        {diff !== null ? (
          <div className="text-xs flex items-center gap-1 mt-1 text-bzgray-500">
            {diff > 0 ? <FiTrendingUp className="w-3 h-3" /> : diff < 0 ? <FiTrendingDown className="w-3 h-3" /> : null}
            {diff > 0 ? '+' : ''}
            {diff.toFixed(1)}% vs forecast
          </div>
        ) : (
          <div className="text-xs text-bzgray-400 mt-1">Forecast unavailable</div>
        )}
      </div>
      <div className="bg-white border border-bzgray-200 rounded-lg p-4">
        <div className="text-xs font-semibold text-bzgray-700 uppercase tracking-wide mb-1">Forecast</div>
        <div className="text-2xl font-bold text-bzgray-900">{fmt(pt?.consensus)}</div>
        <div className="text-xs text-bzgray-500 mt-1">Consensus estimate</div>
      </div>
      <div className="bg-white border border-bzgray-200 rounded-lg p-4">
        <div className="text-xs font-semibold text-bzgray-700 uppercase tracking-wide mb-1">Previous</div>
        <div className="text-2xl font-bold text-bzgray-900">{fmt(pt?.prior)}</div>
        {trend === 'rising' ? (
          <div className="text-xs flex items-center gap-1 mt-1 text-bzgray-500">
            <FiTrendingUp className="w-3 h-3" />
            Rising trend
          </div>
        ) : trend === 'falling' ? (
          <div className="text-xs flex items-center gap-1 mt-1 text-bzgray-500">
            <FiTrendingDown className="w-3 h-3" />
            Falling trend
          </div>
        ) : trend === 'flat' ? (
          <div className="text-xs text-bzgray-500 mt-1">Unchanged from prior</div>
        ) : (
          <div className="text-xs text-bzgray-400 mt-1">Prior unavailable</div>
        )}
      </div>
      <NextReleaseCard nextRelease={nextRelease} latest={latest} />
    </div>
  );
};

const MultiSeriesStatCards = ({
  latest,
  seriesList,
  nextRelease,
}: {
  latest: EconomicsRelease;
  seriesList: SeriesDisplayConfig[];
  nextRelease: Economics | null;
}) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
    {seriesList.map((s, idx) => {
      const pt = latest.series[s.eventName];
      const diff = surpriseDiff(pt);
      const color = getColor(s, idx);
      return (
        <div key={s.eventName} className="bg-white border border-bzgray-200 rounded-lg p-4">
          <div className="text-xs font-semibold text-bzgray-700 uppercase tracking-wide mb-1">{s.label}</div>
          <div className="text-2xl font-bold" style={{ color: pt?.actual != null ? color : undefined }}>
            {fmt(pt?.actual)}
          </div>
          {diff !== null ? (
            <div className="text-xs flex items-center gap-1 mt-1 text-bzgray-500">
              {diff > 0 ? (
                <FiTrendingUp className="w-3 h-3" />
              ) : diff < 0 ? (
                <FiTrendingDown className="w-3 h-3" />
              ) : null}
              {diff > 0 ? '+' : ''}
              {diff.toFixed(2)}% vs est
            </div>
          ) : (
            <div className="text-xs text-bzgray-400 mt-1">Est. unavailable</div>
          )}
        </div>
      );
    })}
    <NextReleaseCard nextRelease={nextRelease} latest={latest} />
  </div>
);

const NextReleaseCard = ({
  nextRelease,
  latest,
}: {
  nextRelease: Economics | null;
  latest: EconomicsRelease | null;
}) => {
  if (!nextRelease && !latest) return null;
  return (
    <div className="bg-white border border-bzgray-200 rounded-lg p-4">
      {nextRelease ? (
        <>
          <div className="text-xs font-semibold text-bzgray-700 uppercase tracking-wide mb-1">Next Release</div>
          <div className="text-lg font-bold text-bzblue-600">
            {new Date(nextRelease.date ?? '').toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            })}
          </div>
          <div className="text-xs text-bzgray-500 mt-1 flex items-center gap-1">
            <FiClock className="w-3 h-3" />
            {nextRelease.event_period} {nextRelease.period_year}
          </div>
        </>
      ) : (
        <>
          <div className="text-xs font-semibold text-bzgray-700 uppercase tracking-wide mb-1">Latest Release</div>
          <div className="text-lg font-bold text-bzblue-600">{latest!.releaseDate}</div>
          <div className="text-xs text-bzgray-500 mt-1 flex items-center gap-1">
            <FiClock className="w-3 h-3" />
            {latest!.period}
          </div>
        </>
      )}
    </div>
  );
};

const SingleSeriesLatestTable = ({
  latest,
  seriesCfg,
}: {
  latest: EconomicsRelease;
  seriesCfg: SeriesDisplayConfig;
}) => {
  const pt = latest.series[seriesCfg.eventName];
  const diff = surpriseDiff(pt);
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-bzgray-100 border-b border-bzgray-200">
          <tr>
            {['Release Date', 'Report Period', 'Actual', 'Forecast', 'Previous', 'Surprise'].map(h => (
              <th
                key={h}
                className={`px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide ${
                  ['Actual', 'Forecast', 'Previous'].includes(h) ? 'text-right' : 'text-left'
                } ${h === 'Surprise' ? 'text-center' : ''} ${
                  h === 'Release Date' ? 'sticky left-0 z-20 bg-bzgray-100 border-r border-bzgray-200' : ''
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="group border-b border-bzgray-100 hover:bg-bzgray-100">
            <td className="px-4 py-2.5 text-sm text-bzgray-900 font-medium sticky left-0 z-10 bg-white group-hover:bg-bzgray-100 border-r border-bzgray-200">
              {latest.releaseDate}
            </td>
            <td className="px-4 py-2.5 text-sm text-bzgray-600">{latest.period}</td>
            <td className="px-4 py-2.5 text-sm text-right font-semibold text-bzgray-900">{fmt(pt?.actual)}</td>
            <td className="px-4 py-2.5 text-sm text-right text-bzgray-600">{fmt(pt?.consensus)}</td>
            <td className="px-4 py-2.5 text-sm text-right text-bzgray-600">{fmt(pt?.prior)}</td>
            <td className="px-4 py-2.5 text-center">
              {diff !== null ? (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${surpriseBadgeClass(diff)}`}
                >
                  {diff > 0 && <FiAlertCircle className="w-3 h-3" />}
                  {diff > 0 ? '+' : ''}
                  {diff.toFixed(1)}%
                </span>
              ) : (
                <span className="text-xs text-bzgray-400">N/A</span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const MultiSeriesLatestTable = ({
  latest,
  seriesList,
}: {
  latest: EconomicsRelease;
  seriesList: SeriesDisplayConfig[];
}) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-bzgray-100 border-b border-bzgray-200">
        <tr>
          <th className="px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide text-left sticky left-0 z-20 bg-bzgray-100 border-r border-bzgray-200">
            Series
          </th>
          {['Actual', 'Forecast', 'Prior', 'Surprise'].map(h => (
            <th
              key={h}
              className={`px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide ${
                h === 'Surprise' ? 'text-center' : 'text-right'
              }`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {seriesList.map((s, idx) => {
          const pt = latest.series[s.eventName];
          const diff = surpriseDiff(pt);
          return (
            <tr key={s.eventName} className="group border-b border-bzgray-100 hover:bg-bzgray-100">
              <td className="px-4 py-2.5 text-sm font-medium text-bzgray-900 sticky left-0 z-10 bg-white group-hover:bg-bzgray-100 border-r border-bzgray-200">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: getColor(s, idx) }} />
                  {s.fullLabel}
                </span>
              </td>
              <td className="px-4 py-2.5 text-sm text-right font-semibold text-bzgray-900">{fmt(pt?.actual)}</td>
              <td className="px-4 py-2.5 text-sm text-right text-bzgray-600">{fmt(pt?.consensus)}</td>
              <td className="px-4 py-2.5 text-sm text-right text-bzgray-600">{fmt(pt?.prior)}</td>
              <td className="px-4 py-2.5 text-center">
                {diff !== null ? (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${surpriseBadgeClass(diff)}`}
                  >
                    {diff > 0 && <FiAlertCircle className="w-3 h-3" />}
                    {diff > 0 ? '+' : ''}
                    {diff.toFixed(2)}%
                  </span>
                ) : (
                  <span className="text-xs text-bzgray-400">N/A</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const HistoricalTable = ({
  releases,
  seriesList,
  primaryName,
}: {
  releases: EconomicsRelease[];
  seriesList: SeriesDisplayConfig[];
  primaryName: string;
}) => {
  const isMulti = seriesList.length > 1;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-bzgray-100 border-b border-bzgray-200">
          <tr>
            <th className="px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide text-left sticky left-0 z-20 bg-bzgray-100 border-r border-bzgray-200">
              Period
            </th>
            {isMulti ? (
              seriesList.map(s => (
                <th
                  key={s.eventName}
                  className="px-4 py-3 text-xs font-semibold text-bzgray-500 uppercase tracking-wide text-right"
                >
                  {s.label}
                </th>
              ))
            ) : (
              <>
                <th className="px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide text-right">
                  Actual
                </th>
                <th className="px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide text-right">
                  Forecast
                </th>
                <th className="px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide text-right">
                  Previous
                </th>
                <th className="px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide text-center">
                  Surprise
                </th>
              </>
            )}
            <th className="px-4 py-2 text-xs font-semibold text-bzgray-500 uppercase tracking-wide text-center">
              Trend
            </th>
          </tr>
        </thead>
        <tbody>
          {[...releases].reverse().map(r => {
            const primaryPt = r.series[primaryName];
            const trend: 'rising' | 'falling' | 'flat' | null =
              primaryPt?.actual != null && primaryPt?.prior != null
                ? primaryPt.actual > primaryPt.prior
                  ? 'rising'
                  : primaryPt.actual < primaryPt.prior
                    ? 'falling'
                    : 'flat'
                : null;
            return (
              <tr key={r.isoDate} className="group border-b border-bzgray-100 hover:bg-bzgray-100">
                <td className="px-4 py-2 text-sm text-bzgray-900 font-medium sticky left-0 z-10 bg-white group-hover:bg-bzgray-100 border-r border-bzgray-200">
                  {r.period || r.releaseDate}
                </td>
                {isMulti ? (
                  seriesList.map(s => {
                    const pt = r.series[s.eventName];
                    const diff = surpriseDiff(pt);
                    return (
                      <td key={s.eventName} className="px-4 py-3 text-sm text-right">
                        {pt?.actual != null ? (
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${surpriseBadgeClass(diff)}`}
                            title={diff === null ? 'No forecast available for this release' : undefined}
                          >
                            {fmt(pt.actual)}
                          </span>
                        ) : (
                          <span className="text-bzgray-400 text-xs">N/A</span>
                        )}
                      </td>
                    );
                  })
                ) : (
                  <>
                    <td className="px-4 py-2.5 text-sm text-right font-semibold text-bzgray-900">
                      {fmt(primaryPt?.actual)}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right text-bzgray-600">{fmt(primaryPt?.consensus)}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-bzgray-600">{fmt(primaryPt?.prior)}</td>
                    <td className="px-4 py-2 text-center">
                      {(() => {
                        const diff = surpriseDiff(primaryPt);
                        return diff !== null ? (
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${surpriseBadgeClass(diff)}`}
                          >
                            {diff > 0 ? '+' : ''}
                            {diff.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-xs text-bzgray-400">N/A</span>
                        );
                      })()}
                    </td>
                  </>
                )}
                <td className="px-4 py-2 text-center">
                  {trend === 'rising' ? (
                    <FiTrendingUp className="w-4 h-4 text-bzgray-500 mx-auto" title="Rising from prior" />
                  ) : trend === 'falling' ? (
                    <FiTrendingDown className="w-4 h-4 text-bzgray-500 mx-auto" title="Falling from prior" />
                  ) : trend === 'flat' ? (
                    <FiMinus className="w-4 h-4 text-bzgray-500 mx-auto" title="Unchanged from prior" />
                  ) : (
                    <span className="text-bzgray-300" title="No prior data">
                      —
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
          {releases.length === 0 && (
            <tr>
              <td colSpan={20} className="px-6 py-8 text-center text-sm text-bzgray-400">
                No historical data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

interface EconomicsPageContentProps extends EconomicsPageData {
  config: EconomicsPageConfig;
}

export default function EconomicsPageContent({
  releases,
  nextRelease,
  news: initialNews,
  config,
}: EconomicsPageContentProps) {
  const {
    name,
    shortName,
    subtitle,
    chartTitle,
    educationalTitle,
    educationalContent,
    Icon = FiInfo,
    source,
    series,
    pageId,
  } = config;
  const session = useContext(SessionContext);
  const { isLoading: isNewsLoading, news } = useEconomicsNews(initialNews);
  useScrollDepthTracking({ page_section: pageId });
  useEffect(() => {
    session.getManager(TrackingManager).trackPageEvent('view', { page_section: pageId });
  }, [pageId, session]);
  const trackClick = (tab: string) => {
    session.getManager(TrackingManager).trackPageEvent('click', {
      page_section: pageId,
      page_tab: tab,
    });
  };
  const latestRelease = releases[releases.length - 1];
  const primaryName = series[0].eventName;
  const isMultiSeries = series.length > 1;
  const chartGroups = Array.from(new Set(series.map(s => s.chartGroup).filter(Boolean) as string[]));
  const hasChartGroups = chartGroups.length > 0;
  const [activeChartGroup, setActiveChartGroup] = useState<string>(hasChartGroups ? chartGroups[0] : '');
  const chartSeries = hasChartGroups
    ? series.filter(s => s.chartGroup === activeChartGroup)
    : series.filter(s => s.showInChart !== false);
  const yAxisLabel = hasChartGroups ? `${activeChartGroup} Change (%)` : 'Change (%)';
  const CHART_GROUP_LONG_NAMES: Record<string, string> = {
    YoY: 'Year-over-Year',
    MoM: 'Month-over-Month',
    QoQ: 'Quarter-over-Quarter',
  };
  const activeGroupLongName = CHART_GROUP_LONG_NAMES[activeChartGroup] ?? activeChartGroup;
  const dynamicChartTitle = hasChartGroups ? `${chartTitle} — ${activeGroupLongName} Change` : chartTitle;
  const chartData = releases.map(r => {
    const point: Record<string, string | number | null> = {
      period: r.period || r.releaseDate,
    };
    chartSeries.forEach(s => {
      point[s.eventName] = r.series[s.eventName]?.actual ?? null;
    });
    return point;
  });
  return (
    <div className="economics-page bg-white">
      <Layout
        className="min-h-screen"
        layoutHeaderOptions={{ full_width: true }}
        sidebarSettings={{ offsetTop: 220, width: 360, visibleOnMobile: true }}
        layoutHeader={
          <CalendarNewLayoutHeader
            breadcrumbs={[
              { id: 'home', href: '/', name: 'Home' },
              { id: 'economic-calendar', href: '/calendars/economic', name: 'Economic Calendar' },
              { id: 'current', href: `/economic-data/${pageId}`, name },
            ]}
            title={name}
            subtitle={subtitle}
            canonical={`https://www.benzinga.com/economic-data/${pageId}`}
            showEdgeBadge={false}
          />
        }
        layoutMain={
          <div className="space-y-8">
            {latestRelease &&
              (isMultiSeries ? (
                <MultiSeriesStatCards latest={latestRelease} seriesList={series} nextRelease={nextRelease} />
              ) : (
                <SingleSeriesStatCards latest={latestRelease} seriesCfg={series[0]} nextRelease={nextRelease} />
              ))}
            {!latestRelease && nextRelease && (
              <div className="grid grid-cols-1 md:grid-cols-3">
                <NextReleaseCard nextRelease={nextRelease} latest={null} />
              </div>
            )}
            {latestRelease && (
              <div className="bg-white border border-bzgray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 border-b border-bzgray-200 bg-bzgray-100">
                  <h2 className="text-lg font-semibold text-bzgray-900">Latest Release Details</h2>
                  {latestRelease.period && (
                    <p className="text-sm text-bzgray-500 mt-0.5">
                      {latestRelease.period} · Released {latestRelease.releaseDate}
                    </p>
                  )}
                </div>
                {isMultiSeries ? (
                  <MultiSeriesLatestTable latest={latestRelease} seriesList={series} />
                ) : (
                  <SingleSeriesLatestTable latest={latestRelease} seriesCfg={series[0]} />
                )}
                <div className="px-4 py-2 bg-bzgray-100 border-t border-bzgray-200 text-xs text-bzgray-500">
                  Source: {source} · Data delivered via{' '}
                  <a
                    href="https://www.benzinga.com/apis"
                    onClick={() => trackClick('apis_attribution')}
                    className="text-bzblue-600 hover:text-bzblue-700 hover:underline"
                  >
                    Benzinga APIs
                  </a>
                </div>
              </div>
            )}
            <div className="bg-white border border-bzgray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <h2 className="text-lg font-semibold text-bzgray-900">{dynamicChartTitle}</h2>
                {hasChartGroups && (
                  <div className="flex items-center gap-1 bg-bzgray-100 rounded-lg p-1">
                    {chartGroups.map(group => (
                      <button
                        key={group}
                        onClick={() => {
                          setActiveChartGroup(group);
                          trackClick(`chart_group_${group.toLowerCase()}`);
                        }}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          activeChartGroup === group
                            ? 'bg-white text-bzblue-600 shadow-sm border border-bzgray-200'
                            : 'text-bzgray-500 hover:text-bzgray-700'
                        }`}
                      >
                        {group}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="period"
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      domain={['auto', 'auto']}
                      tickFormatter={v => `${v}%`}
                      label={{
                        value: yAxisLabel,
                        angle: -90,
                        position: 'insideLeft',
                        style: { fontSize: 12, fill: '#6b7280' },
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {chartSeries.length > 1 && <Legend verticalAlign="top" height={36} />}
                    <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
                    {chartSeries.map((s, idx) => (
                      <Bar
                        key={s.eventName}
                        dataKey={s.eventName}
                        name={s.fullLabel}
                        fill={getColor(s, idx)}
                        shape={<SignAwareBar dataKeyName={s.eventName} />}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-bzgray-400 text-sm">
                  No {shortName} data available
                </div>
              )}
            </div>
            <div className="bg-bzblue-100 border border-bzblue-300 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-bzblue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-bzgray-900">{educationalTitle}</h2>
              </div>
              <div className="text-bzgray-700 space-y-3 text-sm leading-relaxed">{educationalContent}</div>
            </div>
          </div>
        }
        layoutSidebar={
          <div className="economics-sidebar-widget bg-white border border-bzgray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-bzgray-200 bg-bzgray-100 rounded-t-lg">
              <h2 className="text-lg font-semibold text-bzgray-900">Latest Economics News</h2>
              <p className="text-sm text-bzgray-500">From Benzinga News</p>
            </div>
            <div className="divide-y divide-bzgray-100">
              {news.map(article => (
                <NewsItem key={article.id} article={article} onClick={() => trackClick('news_article')} />
              ))}
              {!isNewsLoading && news.length === 0 && (
                <p className="p-4 text-sm text-bzgray-400">No recent news available.</p>
              )}
            </div>
          </div>
        }
        layoutBelow={
          <div className="mt-8 bg-white border border-bzgray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-bzgray-200 bg-bzgray-100 flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-lg font-semibold text-bzgray-900">Historical {shortName} Data</h2>
              <a
                href="https://www.benzinga.com/calendars/economic"
                onClick={() => trackClick('see_full_economic_calendar')}
                className="text-sm font-medium text-blue-500 hover:text-blue-600 hover:underline"
              >
                See full economic calendar →
              </a>
            </div>
            <HistoricalTable releases={releases} seriesList={series} primaryName={primaryName} />
          </div>
        }
      />
    </div>
  );
}
